import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { APP_NAME } from '@teentack/shared';
import { Logo } from '@/components/Logo';
import { BadgeCheckIcon, LockIcon, ShieldCheckIcon, UsersIcon } from '@/components/ui/Icons';

const ASSURANCES = [
  {
    icon: ShieldCheckIcon,
    title: 'Parent-approved by design',
    body: 'Every account under 18 is linked to a guardian before it can apply for work.',
  },
  {
    icon: BadgeCheckIcon,
    title: 'Verified from day one',
    body: 'We confirm your email or mobile with a one-time code the moment you sign up.',
  },
  {
    icon: UsersIcon,
    title: 'Built for three sides',
    body: 'Teens, parents and job posters each get a workspace shaped around them.',
  },
];

/**
 * The signup shell: a brand panel that carries the trust story on large
 * screens, and the wizard itself on the right. Below `lg` the panel collapses
 * to a single line so the form is the first thing on a phone.
 */
export function SignupLayout({ children }: { children: ReactNode }) {
  return (
    <div className="container-page py-8 sm:py-12 lg:py-16">
      <div className="mx-auto grid w-full max-w-6xl gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:items-start lg:gap-16">
        {/* Brand / assurance panel */}
        <aside className="hidden lg:block lg:sticky lg:top-24">
          <div className="overflow-hidden rounded-3xl bg-brand-gradient p-9 text-white shadow-lift">
            <Logo variant="onBrand" />

            <h2 className="mt-8 text-3xl leading-tight font-extrabold text-white">
              Real work. Real trust. Built for teens.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-white/80">
              Setting up an account takes about two minutes. Everything you fill in now shows up on
              your {APP_NAME} profile — and you can change any of it later.
            </p>

            <ul className="mt-9 space-y-6">
              {ASSURANCES.map(({ icon: Icon, title, body }) => (
                <li key={title} className="flex gap-3.5">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
                    <Icon className="size-4.5 text-white" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-white">{title}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-white/75">{body}</span>
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-9 flex items-center gap-2 border-t border-white/15 pt-6 text-xs font-medium text-white/70">
              <LockIcon className="size-4" />
              Your details are never shared without consent.
            </p>
          </div>
        </aside>

        {/* The wizard */}
        <div className="w-full">
          {children}

          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-brand-600 hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
